/**
 * @fileoverview Estilos compartilhados para os modais de criacao e edicao
 * de templates (builder + edit). Design editorial, responsivo (collapse em
 * mobile), suporte a light/dark.
 */

import { makeStyles } from "@material-ui/core/styles";

const isDark = (theme) => theme.palette.type === "dark";

export const useModalStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 18,
      background: isDark(theme) ? "#0f172a" : "#FFFFFF",
      color: isDark(theme) ? "#FFFFFF" : "#0f172a",
      border: isDark(theme)
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(15,23,42,0.08)",
      overflow: "hidden",
      maxWidth: 1000,
      width: "100%",
    },
  },
  dialogInner: {
    display: "flex",
    // Altura fixa: evita reflow ao trocar de aba (Editar <-> Estatisticas)
    // ou de step do builder. O body interno (.body) rola sozinho.
    height: "min(88vh, 680px)",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      height: "92vh",
    },
  },
  formCol: {
    flex: "1 1 58%",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    borderRight: isDark(theme)
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(15,23,42,0.08)",
    [theme.breakpoints.down("sm")]: {
      borderRight: "none",
      borderBottom: isDark(theme)
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(15,23,42,0.08)",
    },
  },
  previewCol: {
    flex: "1 1 42%",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    background: isDark(theme) ? "rgba(255,255,255,0.02)" : "#fafbfc",
    minWidth: 0,
  },
  // header
  header: {
    padding: theme.spacing(3, 3, 1.5, 3),
    borderBottom: isDark(theme)
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(15,23,42,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    position: "relative",
  },
  eyebrow: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: isDark(theme) ? "rgba(255,255,255,0.4)" : "#94a3b8",
  },
  title: {
    fontSize: "1.4rem",
    fontWeight: 700,
    letterSpacing: "-0.015em",
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
  },
  subtitle: {
    fontSize: "0.85rem",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
    lineHeight: 1.45,
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: isDark(theme) ? "rgba(255,255,255,0.6)" : "#64748b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      background: isDark(theme) ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    },
  },
  // stepper
  stepper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 3, 2, 3),
    borderBottom: isDark(theme)
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(15,23,42,0.06)",
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "6px 4px",
    flex: 1,
    minWidth: 0,
  },
  stepNumber: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.72rem",
    fontWeight: 700,
    flexShrink: 0,
    transition: "background 0.2s ease, color 0.2s ease",
  },
  stepNumberActive: {
    background: isDark(theme)
      ? "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)"
      : "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)",
    color: "#FFFFFF",
  },
  stepNumberDone: {
    background: isDark(theme) ? "rgba(16,185,129,0.18)" : "rgba(16,185,129,0.15)",
    color: isDark(theme) ? "#6ee7b7" : "#047857",
  },
  stepNumberIdle: {
    background: isDark(theme) ? "rgba(255,255,255,0.06)" : "#f1f5f9",
    color: isDark(theme) ? "rgba(255,255,255,0.4)" : "#94a3b8",
  },
  stepLabel: {
    fontSize: "0.78rem",
    fontWeight: 500,
    color: isDark(theme) ? "rgba(255,255,255,0.7)" : "#475569",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  stepLabelActive: {
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    fontWeight: 600,
  },
  stepConnector: {
    flex: "0 0 16px",
    height: 1,
    background: isDark(theme) ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
  },
  // body
  body: {
    padding: theme.spacing(3),
    overflowY: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  label: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: isDark(theme) ? "rgba(255,255,255,0.75)" : "#334155",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  labelHint: {
    fontSize: "0.78rem",
    color: isDark(theme) ? "rgba(255,255,255,0.4)" : "#94a3b8",
    fontWeight: 400,
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    fontSize: "0.9rem",
    background: isDark(theme) ? "rgba(255,255,255,0.04)" : "#FFFFFF",
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    boxSizing: "border-box",
    fontFamily: "inherit",
    "&:focus": {
      borderColor: isDark(theme) ? "#818cf8" : "#6366f1",
      boxShadow: isDark(theme)
        ? "0 0 0 3px rgba(99,102,241,0.2)"
        : "0 0 0 3px rgba(99,102,241,0.1)",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
    "&::placeholder": {
      color: isDark(theme) ? "rgba(255,255,255,0.35)" : "#94a3b8",
    },
  },
  textarea: {
    resize: "vertical",
    minHeight: 110,
    lineHeight: 1.5,
  },
  hint: {
    fontSize: "0.78rem",
    color: isDark(theme) ? "rgba(255,255,255,0.45)" : "#64748b",
    lineHeight: 1.45,
  },
  counter: {
    fontSize: "0.72rem",
    fontFamily:
      "ui-monospace, 'SF Mono', 'Menlo', 'Consolas', monospace",
    color: isDark(theme) ? "rgba(255,255,255,0.4)" : "#94a3b8",
  },
  error: {
    fontSize: "0.78rem",
    color: isDark(theme) ? "#fca5a5" : "#dc2626",
  },
  // category cards
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: theme.spacing(1.25),
  },
  choiceCard: {
    padding: theme.spacing(1.75),
    borderRadius: 12,
    cursor: "pointer",
    border: isDark(theme)
      ? "1.5px solid rgba(255,255,255,0.08)"
      : "1.5px solid rgba(15,23,42,0.08)",
    background: isDark(theme) ? "rgba(255,255,255,0.02)" : "#FFFFFF",
    transition: "all 0.15s ease",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    "&:hover": {
      borderColor: isDark(theme)
        ? "rgba(255,255,255,0.18)"
        : "rgba(15,23,42,0.2)",
    },
  },
  choiceCardActive: {
    borderColor: isDark(theme) ? "#818cf8" : "#6366f1",
    background: isDark(theme) ? "rgba(99,102,241,0.1)" : "rgba(99,102,241,0.06)",
    boxShadow: isDark(theme)
      ? "0 0 0 3px rgba(99,102,241,0.15)"
      : "0 0 0 3px rgba(99,102,241,0.08)",
  },
  choiceIcon: {
    color: isDark(theme) ? "#c7d2fe" : "#6366f1",
  },
  choiceTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
  },
  choiceDesc: {
    fontSize: "0.76rem",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
    lineHeight: 1.4,
  },
  // select
  select: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: 10,
    fontSize: "0.9rem",
    background: isDark(theme) ? "rgba(255,255,255,0.04)" : "#FFFFFF",
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:focus": {
      borderColor: isDark(theme) ? "#818cf8" : "#6366f1",
    },
  },
  inlineRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    "& > *": {
      flex: 1,
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  // footer
  footer: {
    padding: theme.spacing(2, 3),
    borderTop: isDark(theme)
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(15,23,42,0.06)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "10px 22px",
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "#FFFFFF",
    background: "linear-gradient(135deg, #0064ff 0%, #0462ca 100%)",
    transition: "filter 0.15s ease, transform 0.15s ease",
    "&:hover:not(:disabled)": {
      filter: "brightness(1.05)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },
  btnGhost: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "10px 18px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: "0.88rem",
    fontWeight: 500,
    background: "transparent",
    border: "none",
    color: isDark(theme) ? "rgba(255,255,255,0.7)" : "#475569",
    "&:hover:not(:disabled)": {
      color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },
  // preview col
  previewTitle: {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: isDark(theme) ? "rgba(255,255,255,0.45)" : "#64748b",
  },
  previewMeta: {
    fontSize: "0.78rem",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
    lineHeight: 1.5,
  },
  sectionDivider: {
    height: 1,
    background: isDark(theme) ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
    margin: theme.spacing(0.5, 0),
  },
  // toggle button (add/remove optional section)
  toggleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    padding: "7px 12px",
    borderRadius: 8,
    fontSize: "0.82rem",
    fontWeight: 500,
    border: isDark(theme)
      ? "1px dashed rgba(255,255,255,0.15)"
      : "1px dashed rgba(15,23,42,0.2)",
    background: "transparent",
    color: isDark(theme) ? "rgba(255,255,255,0.7)" : "#64748b",
    cursor: "pointer",
    alignSelf: "flex-start",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: isDark(theme) ? "#818cf8" : "#6366f1",
      color: isDark(theme) ? "#c7d2fe" : "#4338ca",
    },
  },
  buttonRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  buttonCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(15,23,42,0.08)",
    background: isDark(theme) ? "rgba(255,255,255,0.02)" : "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  buttonCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  buttonCardType: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: isDark(theme) ? "rgba(255,255,255,0.7)" : "#475569",
  },
  removeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: isDark(theme) ? "rgba(255,255,255,0.5)" : "#94a3b8",
    padding: 4,
    borderRadius: 6,
    display: "inline-flex",
    "&:hover": {
      color: isDark(theme) ? "#fca5a5" : "#dc2626",
    },
  },
  // review step
  reviewSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.75, 2),
    borderRadius: 10,
    background: isDark(theme) ? "rgba(255,255,255,0.03)" : "#f8fafc",
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(15,23,42,0.06)",
  },
  reviewLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: isDark(theme) ? "rgba(255,255,255,0.45)" : "#94a3b8",
  },
  reviewValue: {
    fontSize: "0.92rem",
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    fontWeight: 500,
  },
  // tabs (edit)
  tabs: {
    display: "flex",
    gap: 4,
    padding: 4,
    borderRadius: 10,
    background: isDark(theme) ? "rgba(255,255,255,0.04)" : "#f1f5f9",
    alignSelf: "flex-start",
    // respiro em relacao ao header (linha superior) e ao body
    margin: theme.spacing(2, 3, 0.5, 3),
  },
  tab: {
    padding: "7px 14px",
    borderRadius: 7,
    fontSize: "0.82rem",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    background: "transparent",
    color: isDark(theme) ? "rgba(255,255,255,0.6)" : "#64748b",
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    "&:hover": {
      color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    },
  },
  tabActive: {
    background: isDark(theme) ? "rgba(255,255,255,0.08)" : "#FFFFFF",
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    boxShadow: isDark(theme)
      ? "0 1px 3px rgba(0,0,0,0.25)"
      : "0 1px 3px rgba(15,23,42,0.08)",
  },
}));
